import React, { useState, useEffect } from "react";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Button, 
  Modal, 
  Form, 
  InputNumber, 
  Alert,
  Spin,
  Progress,
  Space,
  message,
  Popconfirm
} from "antd";
import { 
  ReloadOutlined, 
  EditOutlined,
  ThunderboltOutlined,
  ToolOutlined,
  HeartOutlined,
  SettingOutlined,
  RocketOutlined,
  DollarOutlined,
  CalculatorOutlined
} from "@ant-design/icons";
import { api } from "@redux/api";
import notification2 from "@components/Notification2";
import moment from "moment";

const FacilityMetrics = ({ record }) => {
  const [loading, setLoading] = useState(false);
  const [metricsData, setMetricsData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editForm] = Form.useForm();

  // 获取性能指标数据
  const fetchMetrics = async () => {
    if (!record?.id) {
      console.error('No facility ID provided');
      return;
    }

    setLoading(true);
    try {
      const response = await api.getFacilityPerformanceMetrics(record.id);
      console.log('Performance Metrics Response:', response.data);
      setMetricsData(response.data);
    } catch (error) {
      console.error('Failed to fetch performance metrics:', error);
      message.error('Failed to fetch performance metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('FacilityMetrics record:', record);
    fetchMetrics();
  }, [record?.id]);

  // Recalculate performance metrics
  const handleRecalculate = async () => {
    if (!record?.id) {
      message.error('No facility ID available');
      return;
    }

    try {
      setCalculating(true);
      const response = await api.triggerAutoCalculation(record.id);

      // Update state with returned data
      if (response && response.data && response.data.data) {
        setMetricsData({
          ...metricsData,
          data: response.data.data
        });
        
        notification2.success({
          message: 'Calculation Complete',
          description: `Performance metrics have been successfully recalculated. Overall Score: ${response.data.overall_score?.toFixed(2)}`,
          placement: 'topRight',
          duration: 4
        });
      }
    } catch (error) {
      console.error('Recalculation failed:', error);
      notification2.error({
        message: 'Calculation Failed',
        description: error.response?.data?.message || error.message || 'Failed to recalculate performance metrics',
        placement: 'topRight'
      });
    } finally {
      setCalculating(false);
    }
  };


  // Get progress bar color
  const getProgressColor = (score) => {
    if (score >= 8) return '#52c41a'; // green
    if (score >= 6) return '#1890ff'; // blue
    if (score >= 4) return '#faad14'; // orange
    return '#ff4d4f'; // red
  };

  // Open single field edit modal
  const handleOpenEditModal = (fieldName, fieldLabel, currentValue) => {
    setEditingField({ name: fieldName, label: fieldLabel });
    editForm.setFieldsValue({ [fieldName]: currentValue });
    setEditModalVisible(true);
  };

  // Submit single field update
  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();
      setSubmitting(true);

      const metricsId = metricsData.data.id;

      // Update single field
      const response = await api.updatePerformanceMetrics(metricsId, values);

      // Update state with returned data instead of fetching again
      if (response && response.data) {
        setMetricsData({
          ...metricsData,
          data: response.data
        });
      }

      message.success(`${editingField.label} updated successfully`);
      setEditModalVisible(false);
    } catch (error) {
      console.error('Update failed:', error);
      if (error.errorFields) {
        message.error('Please check form inputs');
      } else {
        message.error(error.response?.data?.message || 'Failed to update metric');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Render metric card
  const renderMetricCard = (title, value, icon, color) => {
    const score = value || 0;
    return (
      <Card>
        <Statistic
          title={title}
          value={score.toFixed(1)}
          prefix={icon}
          valueStyle={{ color }}
          // suffix="/ 10"
        />
        <Progress
          percent={score * 10}
          strokeColor={getProgressColor(score)}
          showInfo={false}
          style={{ marginTop: 8 }}
        />
      </Card>
    );
  };

  // Render editable metric card
  const renderEditableMetricCard = (title, value, icon, color, fieldName) => {
    const score = value || 0;
    return (
      <Card 
        hoverable
        onClick={() => handleOpenEditModal(fieldName, title, score)}
        style={{ cursor: 'pointer', position: 'relative' }}
      >
        <EditOutlined 
          style={{ 
            position: 'absolute', 
            top: 12, 
            right: 12, 
            fontSize: 16,
            color: '#1890ff'
          }} 
        />
        <Statistic
          title={title}
          value={score.toFixed(1)}
          prefix={icon}
          valueStyle={{ color }}
          // suffix="/ 10"
        />
        <Progress
          percent={score * 10}
          strokeColor={getProgressColor(score)}
          showInfo={false}
          style={{ marginTop: 8 }}
        />
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  if (!metricsData?.data) {
    return (
      <div style={{ padding: '20px' }}>
        <Alert
          message="No Data Available"
          description="No performance metrics data available for this facility"
          type="info"
          showIcon
        />
        </div>
    );
  }

  const data = metricsData.data;
  const performanceBreakdown = metricsData.performance_breakdown || [];

  return (
    <div style={{ padding: '24px' }}>
      {/* Page title and action buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
          Facility Performance Metrics - {record?.name || data.facility_name}
        </h2>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchMetrics} loading={loading}>
            Refresh
          </Button>
          <Popconfirm
            title="Recalculate Performance Metrics"
            description="This will recalculate all auto-computed metrics (Energy Efficiency, Equipment Condition, Management Efficiency, and Cost Efficiency). Continue?"
            onConfirm={handleRecalculate}
            okText="Yes, Recalculate"
            cancelText="Cancel"
            placement="bottomRight"
          >
            <Button 
              type="primary" 
              icon={<CalculatorOutlined />} 
              loading={calculating}
            >
              Recalculate Metrics
            </Button>
          </Popconfirm>
        </Space>
      </div>

      {/* Overall Score */}
      <Card style={{ marginBottom: 24, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '16px', marginBottom: 8, opacity: 0.9 }}>Overall Score</div>
          <div style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: 8 }}>
            {(metricsData?.data?.overall_score || 0)}
          </div>
          <div style={{ fontSize: '14px', opacity: 0.8 }}>
            Last Updated: {moment(metricsData?.data?.last_updated).format('YYYY-MM-DD HH:mm')}
          </div>
        </div>
      </Card>

      {/* 6 Core Metrics */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Energy Efficiency',
            data.energy_efficiency,
            <ThunderboltOutlined />,
            '#faad14'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Equipment Condition',
            data.equipment_condition,
            <ToolOutlined />,
            '#1890ff'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderEditableMetricCard(
            'Occupant Comfort',
            data.occupant_comfort,
            <HeartOutlined />,
            '#eb2f96',
            'occupant_comfort'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Management Efficiency',
            data.management_efficiency,
            <SettingOutlined />,
            '#13c2c2'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderEditableMetricCard(
            'Technology Adoption',
            data.technology_adoption,
            <RocketOutlined />,
            '#722ed1',
            'technology_adoption'
          )}
        </Col>
        <Col xs={24} sm={12} lg={8}>
          {renderMetricCard(
            'Cost Efficiency',
            data.cost_efficiency,
            <DollarOutlined />,
            '#52c41a'
          )}
        </Col>
      </Row>

      {/* Weight Distribution */}
      {/* <Card title="Weight Distribution" style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {performanceBreakdown.map((item, index) => (
            <Col xs={24} sm={12} lg={8} key={index}>
              <div style={{ padding: '12px', background: '#f5f5f5', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 500 }}>{item.name}</span>
                  <span style={{ color: '#1890ff' }}>Weight: {item.value}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#666' }}>
                  <span>Score: {item.score?.toFixed(1)}</span>
                  <span>Percentage: {item.percentage?.toFixed(1)}%</span>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Card> */}

      {/* Detailed Information */}
      {/* <Card title="Detailed Information">
        <Descriptions column={2} bordered>
          <Descriptions.Item label="Facility Name">{data.facility_name}</Descriptions.Item>
          <Descriptions.Item label="Facility ID">{data.facility}</Descriptions.Item>
          <Descriptions.Item label="Energy Efficiency (Auto-calculated)">{data.energy_efficiency?.toFixed(1)}</Descriptions.Item>
          <Descriptions.Item label="Energy Efficiency Weight">{data.energy_efficiency_weight}</Descriptions.Item>
          <Descriptions.Item label="Equipment Condition (Auto-calculated)">{data.equipment_condition?.toFixed(1)}</Descriptions.Item>
          <Descriptions.Item label="Equipment Condition Weight">{data.equipment_condition_weight}</Descriptions.Item>
          <Descriptions.Item label="Occupant Comfort (Manual Input)">{data.occupant_comfort?.toFixed(1)}</Descriptions.Item>
          <Descriptions.Item label="Occupant Comfort Weight">{data.occupant_comfort_weight}</Descriptions.Item>
          <Descriptions.Item label="Management Efficiency (Auto-calculated)">{data.management_efficiency?.toFixed(1)}</Descriptions.Item>
          <Descriptions.Item label="Management Efficiency Weight">{data.management_efficiency_weight}</Descriptions.Item>
          <Descriptions.Item label="Technology Adoption (Manual Input)">{data.technology_adoption?.toFixed(1)}</Descriptions.Item>
          <Descriptions.Item label="Technology Adoption Weight">{data.technology_adoption_weight}</Descriptions.Item>
          <Descriptions.Item label="Cost Efficiency (Auto-calculated)">{data.cost_efficiency?.toFixed(1)}</Descriptions.Item>
          <Descriptions.Item label="Cost Efficiency Weight">{data.cost_efficiency_weight}</Descriptions.Item>
          <Descriptions.Item label="Last Updated">
            {moment(data.last_updated).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
          <Descriptions.Item label="Created Date">
            {moment(data.created_date).format('YYYY-MM-DD HH:mm:ss')}
          </Descriptions.Item>
        </Descriptions>
      </Card> */}

      {/* Single Field Edit Modal */}
      <Modal
        title={`Edit ${editingField?.label || 'Metric'}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        onOk={handleEditSubmit}
        confirmLoading={submitting}
        width={500}
        okText="Save"
        cancelText="Cancel"
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Alert
            message="Note"
            description="Enter a score between 0-10 (supports 1 decimal place)"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Form.Item
            name={editingField?.name}
            label={`${editingField?.label} Score`}
            rules={[
              { required: true, message: 'Please enter a score' },
              { type: 'number', min: 0, max: 10, message: 'Score must be between 0-10' }
            ]}
          >
            <InputNumber
              min={0}
              max={10}
              step={0.1}
              placeholder="Enter score between 0-10"
              style={{ width: '100%' }}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FacilityMetrics;
